<template>
  <div class="start-statistic">
    <b-table
      small
      class="start-statistic-table"
      :fields="fields"
      :items="transactionRows"
      :tbody-tr-class="rowClass"
      responsive="sm"
    >
      <template #cell(createdAt)="data">
        {{ formatDateTime(data.value) }}
      </template>

      <template #cell(amount)="data">
        <span :class="amountClass(data.item)">
          {{ formatAmount(data.value) }}
        </span>
      </template>
    </b-table>

    <b-button
      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
      type="submit"
      variant="primary"
      class="mr-1 save-button"
      :disabled="isloading"
      @click="fetchTransactions"
    >
      <b-spinner
        v-if="isloading"
        small
      />
      {{ isloading ? 'Запрос в банк...' : 'Запросить информацию из банка' }}
    </b-button>

    <div class="start-statistic-summary mt-1">
      <div
        v-if="hasRefundSummary"
        class="summary-line"
      >
        Оплачено: {{ transactions.grossSum }} руб.
        <span class="summary-refund"> · Возвраты: −{{ transactions.refundSum }} руб.</span>
      </div>
      <div class="summary-total">
        Итого (с учётом возвратов): <strong>{{ transactions.sum || 0 }} руб.</strong>
      </div>
    </div>
  </div>
</template>

<script>
import { BTable, BSpinner, BButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'StartStatistic',
  components: {
    BTable,
    BSpinner,
    BButton,
  },
  directives: {
    Ripple,
  },
  props: {
    startId: {
      type: Number,
      required: true,
    },
    transactionsData: {
      type: Object,
      default: null,
    },
    autoFetch: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isloading: false,
      fields: [
        { key: 'createdAt', label: 'Дата', sortable: false },
        { key: 'orderNumber', label: 'Номер заказа' },
        { key: 'payerData.email', label: 'Описание' },
        { key: 'bankInfo.bankName', label: 'Способ оплаты' },
        { key: 'actionCodeDescription', label: 'Статус' },
        { key: 'amount', label: 'Сумма, руб.' },
      ],
      transactions: {
        sum: 0,
        grossSum: 0,
        refundSum: 0,
        rows: [],
      },
    }
  },
  computed: {
    transactionRows() {
      return this.transactions.rows || []
    },
    hasRefundSummary() {
      return (this.transactions.refundSum || 0) > 0
    },
  },
  watch: {
    transactionsData: {
      immediate: true,
      handler(value) {
        if (value) {
          this.transactions = {
            sum: value.sum ?? 0,
            grossSum: value.grossSum ?? value.sum ?? 0,
            refundSum: value.refundSum ?? 0,
            rows: value.rows || [],
          }
        }
      },
    },
  },
  created() {
    if (this.autoFetch && !this.transactionsData) {
      this.fetchTransactions()
    }
  },
  methods: {
    formatDateTime(value) {
      if (!value) {
        return '—'
      }

      return new Date(value).toLocaleString('ru', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatAmount(amountKopecks) {
      const rub = amountKopecks / 100
      const formatted = Number.isInteger(rub) ? rub.toString() : rub.toFixed(2)
      return rub > 0 ? `+${formatted}` : formatted
    },
    amountClass(item) {
      if (item.rowType === 'refund' || item.rowType === 'slot_refund' || item.actionCode === 2) {
        return 'amount-refund'
      }
      return 'amount-payment'
    },
    rowClass(item, type) {
      if (!item || type !== 'row') {
        return null
      }
      if (item.rowType === 'refund' || item.rowType === 'slot_refund' || item.actionCode === 2) {
        return 'row-refund'
      }
      if (item.rowType === 'payment' || item.actionCode === 0) {
        return 'row-payment'
      }
      return null
    },
    async fetchTransactions() {
      if (!Number.isFinite(this.startId) || this.startId <= 0) {
        return
      }
      this.isloading = true
      try {
        this.transactions = await this.$http
          .get(`/member-start/${this.startId}/transactions`)
          .then(r => r.data)
        this.$emit('loaded', this.transactions)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Уведомление',
            icon: 'BellIcon',
            text: 'Ошибка получения информации из банка',
            variant: 'danger',
          },
        })
      } finally {
        this.isloading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.start-statistic-table {
  ::v-deep table {
    color: #5e5873;
  }

  ::v-deep thead th {
    color: #5e5873;
    background-color: #f8f8f8;
    border-color: #ebe9f1;
  }

  ::v-deep tbody td {
    border-color: #ebe9f1;
    vertical-align: middle;
  }

  ::v-deep tr.row-payment td {
    background-color: rgba(40, 199, 111, 0.14);
  }

  ::v-deep tr.row-refund td {
    background-color: rgba(234, 84, 85, 0.14);
  }
}

.amount-payment {
  color: #28a745;
  font-weight: 600;
}

.amount-refund {
  color: #ea5455;
  font-weight: 600;
}

.start-statistic-summary {
  color: #5e5873;
}

.summary-refund {
  color: #ea5455;
}

.summary-total {
  margin-top: 0.25rem;
}
</style>

<style lang="scss">
.dark-layout .start-statistic-table table,
.dark-layout .start-statistic-table thead th,
.dark-layout .start-statistic-table tbody td,
.dark-layout .start-statistic-summary {
  color: #d0d2d6 !important;
}

.dark-layout .start-statistic-table thead th {
  background-color: #283046 !important;
  border-color: #3b4253 !important;
}

.dark-layout .start-statistic-table tbody td {
  border-color: #3b4253 !important;
}

.dark-layout .start-statistic-table tr.row-payment td {
  background-color: rgba(40, 199, 111, 0.18) !important;
}

.dark-layout .start-statistic-table tr.row-refund td {
  background-color: rgba(234, 84, 85, 0.18) !important;
}

.dark-layout .amount-payment {
  color: #55d67a !important;
}

.dark-layout .amount-refund,
.dark-layout .summary-refund {
  color: #ff8b8c !important;
}
</style>
